// Custom fetch for openapi-fetch `createClient({ fetch })`, mirroring educator's
// `freckleFetch` (megarepo/frontend/educator/entities/ts/common/helpers/ajax/index.ts).
//
// Behaviours ported:
// - credentials: 'include' baked into the Request (XSRF parity with the existing
//   jQuery `$.ajaxSetup({ xhrFields: { withCredentials: true } })` in
//   console/src/helpers/api-helpers.ts).
// - AbortController with optional `timeout` (ms) from the fetch init.
// - x-echo query param stamped on every request, which the Fancy API's
//   `respondToEcho` middleware (backend/fancy-api/library/Application.hs)
//   round-trips in an `X-Echo` response header. We compare the two and throw
//   on a mismatch, so a stale caching proxy or a response crossing in from
//   another user is never handed to the caller as its own data. Skippable via
//   the `excludeEcho` option.
// - Delegates response.ok branching to ./error.ts; anything else `fetch` throws
//   (network failure, abort, malformed request) propagates unchanged.
// - Leaves a Bugsnag breadcrumb on every failed request, mirroring the
//   `beforeSend`/`error` instrumentation in api-helpers.ts's `setupAjax()` so
//   moving a consumer off `ajaxJsonCall` doesn't silently drop that visibility.

import {leaveAjaxErrorBreadcrumb} from '@frontrow/console/src/entities/helpers/exception-handlers/bugsnag-helper'

import {EchoMismatchError, FetchError, throwForEchoMismatch, throwForHttpError} from './error'

type ExtendedInit = RequestInit & {
  excludeEcho?: boolean
  timeout?: number
}

/**
 * Custom fetch compatible with `openapi-fetch`'s `fetch` option. Takes the same
 * (input, init) signature as the global `fetch`, with two extra init keys
 * (`excludeEcho`, `timeout`) matching educator's wrapper.
 */
export const fancyApiFetch = async (
  input: string | URL | Request,
  init?: ExtendedInit
): Promise<Response> => {
  const {excludeEcho = false, timeout, ...fetchInit} = init ?? {}

  const controller = new AbortController()
  const timeoutId =
    timeout !== undefined ? setTimeout(() => controller.abort(), timeout) : undefined

  const request = new Request(input, {
    credentials: 'include',
    signal: controller.signal,
    ...fetchInit
  })

  const requestStartTime = Date.now()

  const echo = excludeEcho ? undefined : stampEcho(request.url)

  try {
    const req =
      echo !== undefined
        ? new Request(echo.url, {
            method: request.method,
            keepalive: request.keepalive,
            headers: request.headers,
            body: ['GET', 'HEAD'].includes(request.method)
              ? undefined
              : await request.clone().arrayBuffer(),
            redirect: request.redirect,
            integrity: request.integrity,
            signal: request.signal,
            credentials: request.credentials,
            mode: request.mode,
            referrer: request.referrer,
            referrerPolicy: request.referrerPolicy
          })
        : request

    const response = await window.fetch(req)
    // Checked ahead of the status: a response carrying someone else's echo
    // must not be read at all, not even for its status code.
    if (echo !== undefined) throwForEchoMismatch(request, response, echo.value)
    return await throwForHttpError(request, response)
  } catch (e) {
    leaveFetchErrorBreadcrumb(request, e, requestStartTime)
    throw e
  } finally {
    if (timeoutId !== undefined) clearTimeout(timeoutId)
  }
}

/**
 * Returns the request URL with a fresh `x-echo` query param, alongside the value
 * stamped on it so the response can be checked against it. A new UUID per
 * request rather than one per page load: a constant would only catch a response
 * crossing in from a different browser session, not two of this page's own
 * in-flight requests swapping responses. Returns undefined when `new URL`
 * throws, in which case the request is issued unmodified and unchecked.
 */
function stampEcho(url: string): {url: URL; value: string} | undefined {
  const value = crypto.randomUUID()
  try {
    const withEcho = new URL(url)
    // Replace rather than append: pagination link URLs handed back by the API
    // still carry the echo of the request that produced them.
    withEcho.searchParams.delete('x-echo')
    withEcho.searchParams.append('x-echo', value)
    return {url: withEcho, value}
  } catch {
    return undefined
  }
}

function leaveFetchErrorBreadcrumb(
  request: Request,
  error: unknown,
  requestStartTime: number
): void {
  const status =
    error instanceof FetchError || error instanceof EchoMismatchError ? error.status : 0
  const name = error instanceof Error ? error.name : 'UnknownError'
  const message = error instanceof Error ? error.message : String(error)

  leaveAjaxErrorBreadcrumb({
    reqType: request.method,
    reqUrl: request.url,
    reqStatus: {status: status.toString(), textStatus: name},
    totalDurationMs: Date.now() - requestStartTime,
    // `fetch` has no equivalent of XHR's OPENED readyState to isolate
    // server-side duration from total duration, unlike the jQuery path this
    // is replacing.
    serverDurationMs: 0,
    errorThrown: message
  })
}
